import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Badge } from 'react-bootstrap';
import InitialAvatar from '../InitialAvatar';
import { getRefreshHistory, getPORefreshHistory, getQuoteRefreshHistory, getUsersByEmail } from '../../context/CommonContex'; // Import your API function

const HISTORY_PANEL_WIDTH = 320;
const HISTORY_PANEL_MAX_HEIGHT = 400;

const OrderRefresh = ({ onRefresh, lastRefreshData, isLoading, title = "Orders", refreshHistoryFunction = null }) => {
    const [refreshCount, setRefreshCount] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshHistory, setRefreshHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [panelStyle, setPanelStyle] = useState(null);
    const historyButtonRef = useRef(null);

    // Determine which history function to use based on title
    const getHistoryFunction = () => {
        if (refreshHistoryFunction) return refreshHistoryFunction;
        
        if (title === "Quotes") return getQuoteRefreshHistory;
        if (title === "Purchase Orders") return getPORefreshHistory;
        return getRefreshHistory; // Default for Orders
    };



    // Load refresh history from API
    const loadRefreshHistory = async () => {
        try {
            setLoadingHistory(true);
            const historyFunction = getHistoryFunction();
            const historyData = await historyFunction();
            console.log(`📋 [OrderRefresh] Loaded ${title} refresh history:`, historyData);
            if (historyData && Array.isArray(historyData)) {
                setRefreshHistory(historyData);
                setRefreshCount(historyData.length);
            }
        } catch (error) {
            console.error(`Error loading ${title} refresh history:`, error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const updatePanelPosition = useCallback(() => {
        const button = historyButtonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const gutter = 8;
        const width = Math.min(HISTORY_PANEL_WIDTH, Math.max(220, window.innerWidth - gutter * 2));
        let left = rect.right - width;
        if (left < gutter) left = gutter;
        if (left + width > window.innerWidth - gutter) {
            left = Math.max(gutter, window.innerWidth - width - gutter);
        }

        const spaceBelow = window.innerHeight - rect.bottom - gutter;
        const spaceAbove = rect.top - gutter;
        const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
        const maxHeight = Math.min(
            HISTORY_PANEL_MAX_HEIGHT,
            Math.max(160, openUp ? spaceAbove - 6 : spaceBelow - 6)
        );
        const top = openUp ? rect.top - maxHeight - 6 : rect.bottom + 6;

        setPanelStyle({
            position: 'fixed',
            top,
            left,
            width,
            maxHeight,
            zIndex: 1000005,
            transform: 'none',
            marginTop: 0,
            right: 'auto',
        });
    }, []);

    useLayoutEffect(() => {
        if (!showHistory) {
            setPanelStyle(null);
            return undefined;
        }
        updatePanelPosition();
        window.addEventListener('resize', updatePanelPosition);
        window.addEventListener('scroll', updatePanelPosition, true);
        return () => {
            window.removeEventListener('resize', updatePanelPosition);
            window.removeEventListener('scroll', updatePanelPosition, true);
        };
    }, [showHistory, updatePanelPosition]);

    // Close history panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = event.target.closest('.refresh-history-dropdown');
            const panel = event.target.closest('.refresh-history-panel');
            if (showHistory && !dropdown && !panel) {
                setShowHistory(false);
            }
        };

        if (showHistory) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showHistory]);

    // Load history when component mounts
    useEffect(() => {
        loadRefreshHistory();
    }, []);

    // Handle refresh button click
const handleRefresh = async () => {
    const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email');

    if (!userEmail) {
        alert('User session not found. Please login again.');
        return;
    }

    try {
        const result = await getUsersByEmail(userEmail);
        const hasAccess = title === 'Purchase Orders'
            ? result.success && result.poValidationAccess
            : result.success && result.multifactorEnabled;

        if (!hasAccess) {
            alert('You don\'t have access to refresh the orders');
            return;
        }
    } catch (error) {
        console.error('Error checking user permissions:', error);
        alert('Error checking permissions. Please try again.');
        return;
    }

    // Check if refresh count has reached the limit
    if (refreshCount >= 1000) {
        console.log('❌ Refresh limit reached - showing alert');
        alert('Maximum refresh limit of 10 reached.');
        return;
    }

    if (isRefreshing) {
        console.log('⚠️ Already refreshing, returning');
        return;
    }

    console.log('🔄 Starting refresh process...');
    setIsRefreshing(true);
    setRefreshCount(prev => prev + 1);

    // Add to refresh history
    const refreshUser = localStorage.getItem('userEmail') || localStorage.getItem('userName') || 'Current User';
    const refreshEntry = {
        id: Date.now(),
        timestamp: new Date(),
        user: refreshUser,
        userName: refreshUser,
        status: 'pending'
    };

    setRefreshHistory(prev => [refreshEntry, ...prev.slice(0, 9)]);

    try {
        console.log('📞 Calling onRefresh...');
        await onRefresh();

        // Update the refresh entry status
        setRefreshHistory(prev =>
            prev.map(entry =>
                entry.id === refreshEntry.id
                    ? { ...entry, status: 'success' }
                    : entry
            )
        );

        // Reload history from API after successful refresh
        await loadRefreshHistory();
        console.log('✅ Refresh completed successfully');
    } catch (error) {
        console.error('❌ Refresh failed:', error);

        // Update the refresh entry status
        setRefreshHistory(prev =>
            prev.map(entry =>
                entry.id === refreshEntry.id
                    ? { ...entry, status: 'error' }
                    : entry
            )
        );
    } finally {
        console.log('🏁 Refresh process finished');
        setIsRefreshing(false);
    }
};

    // Format timestamp for display
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    };

    // Get status icon and color
    const getStatusDisplay = (status) => {
        switch (status) {
            case 'success':
                return { icon: '✓', color: 'success', text: 'Success' };
            case 'error':
                return { icon: '✗', color: 'danger', text: 'Failed' };
            case 'pending':
                return { icon: '⟳', color: 'warning', text: 'Pending' };
            default:
                return { icon: '?', color: 'secondary', text: 'Unknown' };
        }
    };

    // Format time for detailed display
    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    };

    // Format date for detailed display
    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="order-refresh-container">
            {/* Main Refresh Button - Only show for authorized users */}
                <div className="refresh-button-section">
                    <Button
                        variant={isRefreshing ? "warning" : "primary"}
                        size="sm"
                        onClick={handleRefresh}
                        disabled={isRefreshing || isLoading}
                        className="refresh-button"
                        style={{
                            minWidth: '120px',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {isRefreshing ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Refreshing...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-sync-alt me-2"></i>
                                Refresh
                            </>
                        )}

                        {/* Animated background effect */}
                        {isRefreshing && (
                            <div
                                className="refresh-progress"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    width: '100%',
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                    animation: 'refreshShimmer 1.5s infinite'
                                }}
                            ></div>
                        )}
                    </Button>

                    {/* Refresh Counter Badge */}
                    {refreshCount > 0 && (
                        <Badge
                            bg="info"
                            className="ms-2 refresh-counter"
                            style={{ fontSize: '0.75rem' }}
                        >
                            {refreshCount}
                        </Badge>
                    )}

                </div>
            
            {/* Last Refresh Info - Show for all users */}
            {lastRefreshData && (
                <div className="last-refresh-info">
                    <div className="refresh-status-indicator">
                        <span className="text-muted">
                            <i className="fas fa-clock me-1"></i>
                            {formatTimestamp(lastRefreshData.timestamp)}
                        </span>
                        {lastRefreshData.user && (
                            <span className="text-muted">
                                <i className="fas fa-user me-1"></i>
                                {lastRefreshData.user}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Refresh History Dropdown - Show for all users */}
            {refreshHistory.length > 0 && (
                <div className="refresh-history-dropdown">
                    <span ref={historyButtonRef} className="d-inline-block">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="history-button"
                        style={{ fontSize: '0.75rem' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowHistory(!showHistory);
                            if (!showHistory) {
                                loadRefreshHistory();
                            }
                        }}
                        disabled={loadingHistory}
                        title="Click to view refresh history"
                    >
                        {loadingHistory ? (
                            <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                            ></span>
                        ) : (
                            <i className="fas fa-history me-1"></i>
                        )}
                        History
                    </Button>
                    </span>

                    {showHistory && panelStyle && createPortal(
                        <div
                            className="refresh-history-panel show refresh-history-panel-portal"
                            style={panelStyle}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <div className="history-header">
                                <h6 className="mb-2">Refresh History</h6>
                            </div>
                            <div className="history-list">
                                {refreshHistory.map((entry) => {
                                    const statusDisplay = getStatusDisplay(entry.status);
                                    return (
                                        <div key={entry.id} className="history-item">
                                            <div className="history-item-content">
                                                {/* <div className="history-status">
                                                    <Badge
                                                        bg={statusDisplay.color}
                                                        style={{ fontSize: '0.6rem' }}
                                                    >
                                                        {statusDisplay.icon}
                                                    </Badge>
                                                </div> */}
                                                <div style={{ flexShrink: 0 }}>
                                                    <InitialAvatar name={entry.userName || entry.user} size={32} />
                                                </div>
                                                <div className="history-details">
                                                    <div className="history-user">
                                                        {entry.userName || entry.user}
                                                    </div>
                                                    {entry.startTime && (
                                                        <div className="history-full-time">
                                                            {formatDate(entry.startTime)} {formatTime(entry.startTime)}
                                                        </div>
                                                    )}
                                                    {entry.durationSeconds > 0 && (
                                                        <div className="history-duration">
                                                            {/* Duration: {entry.durationSeconds} */}
                                                            Duration: {Math.floor(entry.durationSeconds / 60)}m {entry.durationSeconds % 60}s
                                                        </div>
                                                    )}
                                                    {/* {entry.count !== undefined && (
                                                        <div className="history-count">
                                                            Records: {entry.count}
                                                        </div>
                                                    )} */}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>,
                        document.body
                    )}
                </div>
            )}

            <style jsx>{`
                .history-item-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 8px;
                }
                .history-status {
                    flex-shrink: 0;
                }
                .history-details {
                    flex-grow: 1;
                }
                .history-time {
                    font-weight: bold;
                    font-size: 0.8rem;
                }
                .history-user {
                    font-size: 0.75rem;
                    color: #666;
                }
                .history-full-time,
                .history-duration,
                .history-count {
                    font-size: 0.7rem;
                    color: #888;
                }
                .refresh-history-panel-portal .history-list {
                    overflow-y: auto;
                }
                @keyframes refreshShimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
            `}</style>
        </div>
    );
};

export default OrderRefresh;